import AccessTime from '@mui/icons-material/AccessTime';
import LunchDining from '@mui/icons-material/LunchDining';
import Logout from '@mui/icons-material/Logout';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import PersonOff from '@mui/icons-material/PersonOff';
import PhoneDisabled from '@mui/icons-material/PhoneDisabled';
import PhoneInTalk from '@mui/icons-material/PhoneInTalk';
import PortableWifiOff from '@mui/icons-material/PortableWifiOff';
import TaskAlt from '@mui/icons-material/TaskAlt';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { UserTelephonyAvailability } from '../../types';

interface UserTelephonyAvailabilityStatusProps {
  availability?: UserTelephonyAvailability | null;
  nowMs?: number;
  showTimer?: boolean;
  showIneligibleState?: boolean;
  direction?: 'row' | 'column';
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  chipSize?: 'small' | 'medium';
  timerColor?: string;
  gap?: number;
  timerFontSize?: string | number;
  chipSx?: Record<string, unknown>;
}

interface AvailabilityPresentation {
  label: string;
  icon: ReactElement;
  chipSx: Record<string, unknown>;
}

const TIMER_WIDTH_RESERVATION = '00:00:00';

function clampElapsedSeconds(unavailableSince: string | null | undefined, nowMs: number): number | null {
  if (!unavailableSince) {
    return null;
  }
  const parsed = new Date(unavailableSince);
  const startedAtMs = parsed.getTime();
  if (Number.isNaN(startedAtMs)) {
    return null;
  }
  return Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
}

function formatAvailabilityTimerDuration(
  elapsedSeconds: number,
  formatDuration: (seconds: number | undefined | null, isAverage?: boolean, includeMilliseconds?: boolean) => string,
): string {
  /**
   * The shared availability timer stays compact below one hour and switches to
   * `HH:MM:SS` once the pause grows, so operational surfaces do not show
   * unreadable long minute-only counters such as `256:00`.
   */
  if (elapsedSeconds < 3600) {
    return formatDuration(elapsedSeconds);
  }

  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':');
}

/**
 * Render the canonical telephony-availability chip/timer pair used by header,
 * list, and detail surfaces.
 */
export default function UserTelephonyAvailabilityStatus({
  availability,
  nowMs,
  showTimer = false,
  showIneligibleState = true,
  direction = 'column',
  alignItems,
  chipSize = 'small',
  timerColor,
  gap,
  timerFontSize,
  chipSx,
}: UserTelephonyAvailabilityStatusProps) {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const { formatDuration } = useNumberFormat();

  const effectiveNowMs = nowMs ?? Date.now();
  const elapsedSeconds = useMemo(
    () => clampElapsedSeconds(availability?.unavailableSince, effectiveNowMs),
    [availability?.unavailableSince, effectiveNowMs],
  );

  const presentation = useMemo<AvailabilityPresentation | null>(() => {
    if (!availability) {
      return null;
    }
    if (availability.effectiveStatus === 'logged_out') {
      return {
        label: t('common:operatorAvailability.states.loggedOut'),
        icon: <Logout fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.error.main, 0.14),
          color: theme.palette.error.light,
          borderColor: alpha(theme.palette.error.main, 0.28),
        },
      };
    }
    if (availability.effectiveStatus === 'presence_expired') {
      return {
        label: t('common:operatorAvailability.states.presenceExpired'),
        icon: <PersonOff fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.info.main, 0.16),
          color: theme.palette.info.light,
          borderColor: alpha(theme.palette.info.main, 0.3),
        },
      };
    }
    if (!availability?.isEligible) {
      if (!showIneligibleState) {
        return null;
      }
      return {
        label: t('common:operatorAvailability.states.noEndpoint'),
        icon: <PhoneDisabled fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.text.secondary, 0.12),
          color: theme.palette.text.secondary,
          borderColor: alpha(theme.palette.text.secondary, 0.28),
        },
      };
    }

    if (availability.effectiveStatus === 'operator_idle') {
      return {
        label: t('common:operatorAvailability.states.operatorIdle'),
        icon: <AccessTime fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.warning.main, 0.18),
          color: theme.palette.warning.main,
          borderColor: alpha(theme.palette.warning.main, 0.36),
        },
      };
    }

    if (availability.effectiveStatus === 'operator_hidden') {
      return {
        label: t('common:operatorAvailability.states.operatorHidden'),
        icon: <VisibilityOff fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.warning.main, 0.18),
          color: theme.palette.warning.light,
          borderColor: alpha(theme.palette.warning.main, 0.36),
        },
      };
    }

    if (availability.effectiveStatus === 'operator_needs_confirmation') {
      return {
        label: t('common:operatorAvailability.states.operatorNeedsConfirmation'),
        icon: <TaskAlt fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.info.main, 0.16),
          color: theme.palette.info.light,
          borderColor: alpha(theme.palette.info.main, 0.3),
        },
      };
    }

    if (availability.isAvailable) {
      return {
        label: t('common:operatorAvailability.states.available'),
        icon: <PhoneInTalk fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.success.main, 0.14),
          color: theme.palette.success.main,
          borderColor: alpha(theme.palette.success.main, 0.32),
        },
      };
    }

    if (availability.reason === 'lunch') {
      return {
        label: t('common:operatorAvailability.states.lunch'),
        icon: <LunchDining fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.warning.main, 0.18),
          color: theme.palette.warning.light,
          borderColor: alpha(theme.palette.warning.main, 0.36),
        },
      };
    }

    if (availability.reason === 'break') {
      return {
        label: t('common:operatorAvailability.states.break'),
        icon: <PauseCircleFilled fontSize="small" />,
        chipSx: {
          bgcolor: alpha(theme.palette.warning.main, 0.18),
          color: theme.palette.warning.main,
          borderColor: alpha(theme.palette.warning.main, 0.36),
        },
      };
    }

    return {
      label: t('common:operatorAvailability.states.unavailable'),
      icon: <PortableWifiOff fontSize="small" />,
      chipSx: {
        bgcolor: alpha(theme.palette.warning.main, 0.18),
        color: theme.palette.warning.main,
        borderColor: alpha(theme.palette.warning.main, 0.36),
      },
    };
  }, [availability, showIneligibleState, t, theme]);

  if (!presentation) {
    return null;
  }

  const shouldShowTimer = Boolean(
    showTimer
      && availability?.isEligible
      && !availability?.isAvailable
      && elapsedSeconds != null,
  );
  const timerDuration = shouldShowTimer && elapsedSeconds != null
    ? formatAvailabilityTimerDuration(elapsedSeconds, formatDuration)
    : null;
  const timerLabel = timerDuration
    ? t('common:operatorAvailability.menu.currentTimer', {
      duration: timerDuration,
    })
    : null;
  const timerReservationLabel = t('common:operatorAvailability.menu.currentTimer', {
    duration: TIMER_WIDTH_RESERVATION,
  });

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: direction,
        alignItems: alignItems ?? (direction === 'row' ? 'center' : 'flex-start'),
        gap: gap ?? (direction === 'row' ? 1 : 0.6),
        minWidth: 0,
      }}
    >
      <Chip
        icon={presentation.icon}
        label={presentation.label}
        size={chipSize}
        variant="outlined"
        sx={{ ...presentation.chipSx, ...chipSx }}
      />
      {shouldShowTimer ? (
        <Box sx={{ display: 'grid', alignItems: 'start' }}>
          <Typography
            aria-hidden
            variant="caption"
            sx={{
              gridArea: '1 / 1',
              visibility: 'hidden',
              pointerEvents: 'none',
              userSelect: 'none',
              whiteSpace: 'nowrap',
              fontSize: timerFontSize,
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {timerReservationLabel}
          </Typography>
          <Typography
            variant="caption"
            color={timerColor || 'text.secondary'}
            sx={{
              gridArea: '1 / 1',
              whiteSpace: 'nowrap',
              fontSize: timerFontSize,
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {timerLabel}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
